import { useMemo } from "react";
import { Group, Select } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import type { ProductSetToLabel } from "@labeling-tools/api/field-rep/generated/types.gen";

type ProductSetSelectProps = {
  /** The product sets with tasks waiting. undefined while loading. */
  productSets: ProductSetToLabel[] | undefined;
  selectedProductSetId: string | null;
  disabled: boolean;
  onSelect: (productSetId: string) => void;
};

/**
 * A dropdown for the product set to label. Labeling needs one. The passed in
 * selectedProductSetId is always included as an option, even when it isn't in
 * productSets.
 */
export const ProductSetSelect = ({
  productSets,
  selectedProductSetId,
  disabled,
  onSelect,
}: ProductSetSelectProps) => {
  const pendingCountById = useMemo(
    () =>
      new Map(
        (productSets ?? []).map((productSet) => [
          productSet.id,
          productSet.pending_count,
        ]),
      ),
    [productSets],
  );

  const selectOptions = useMemo(() => {
    const options = (productSets ?? []).map((productSet) => ({
      value: productSet.id,
      label: productSet.name,
    }));

    // The selectedProductSetId won't be in the product sets once all tasks are completed.
    // But we still render as an option.
    if (selectedProductSetId && !pendingCountById.has(selectedProductSetId)) {
      options.push({
        value: selectedProductSetId,
        label: "No tasks waiting in this set",
      });
    }
    return options;
  }, [pendingCountById, selectedProductSetId, productSets]);

  return (
    <Select
      className="w-72"
      size="md"
      radius="md"
      allowDeselect={false}
      disabled={disabled}
      data={selectOptions}
      placeholder="Pick a product set"
      value={selectedProductSetId}
      onChange={(value) => {
        if (value) {
          onSelect(value);
        }
      }}
      renderOption={({ option, checked }) => {
        const pendingCount = pendingCountById.get(option.value);
        return (
          <Group gap="xs" wrap="nowrap" className="w-full">
            {checked && <IconCheck size={14} />}
            <span className="flex-1 truncate text-xs">{option.label}</span>
            {pendingCount !== undefined && (
              <span className="text-xs tabular-nums text-[#94A3B8]">
                {pendingCount.toLocaleString()}
              </span>
            )}
          </Group>
        );
      }}
    />
  );
};
